if (!customElements.get('size-finder')) {
  customElements.define(
    'size-finder',
    class SizeFinder extends HTMLElement {
      constructor() {
        super();
        this.strings = (window.FoxTheme && window.FoxTheme.sizeFinderStrings) || {};
      }

      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.chartData = this.parseJSON(this.querySelector('[data-size-chart]'));
        this.variants = this.parseJSON(this.querySelector('[data-product-variants]')) || [];
        this.sizeOptionPosition = this.dataset.sizeOptionPosition !== '' ? Number(this.dataset.sizeOptionPosition) : null;
        this.productId = this.dataset.productId;

        this.liveRegion = this.querySelector('[data-live-region]');

        this.populateMeasurementSelect('bust', 28, 42);
        this.populateMeasurementSelect('waist', 20, 36);
        this.populateMeasurementSelect('hips', 30, 48);

        this.categoryTabs = Array.from(this.querySelectorAll('[data-category-tab]'));
        this.categoryTabs.forEach((tab) => {
          tab.addEventListener('click', () => this.setCategory(tab.dataset.categoryTab), { signal });
        });

        this.secondaryTabs = Array.from(this.querySelectorAll('[data-view-tab]'));
        this.secondaryTabs.forEach((tab) => {
          tab.addEventListener('click', () => this.setView(tab.dataset.viewTab), { signal });
        });

        this.querySelectorAll('[data-quiz]').forEach((form) => {
          form.addEventListener('submit', this.onSubmit.bind(this), { signal });
          form.querySelectorAll('select[data-field]').forEach((select) => {
            select.addEventListener('change', () => this.validateForm(form), { signal });
          });
        });

        this.querySelectorAll('[data-recalculate]').forEach((button) => {
          button.addEventListener('click', (event) => this.onRecalculate(event), { signal });
        });

        this.querySelectorAll('[data-select-size]').forEach((button) => {
          button.addEventListener('click', (event) => this.onSelectSize(event), { signal });
        });

        this.setCategory(this.dataset.defaultCategory || 'tops');
        this.setView('quiz');
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      parseJSON(node) {
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error('size-finder: failed to parse JSON', error);
          return null;
        }
      }

      populateMeasurementSelect(field, min, max) {
        const select = this.querySelector(`select[data-field="${field}"]`);
        if (!select || select.dataset.populated) return;

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.selected = true;
        placeholder.textContent = this.strings.selectOrEnter || 'Select or enter';
        select.appendChild(placeholder);

        for (let value = min; value <= max; value += 0.5) {
          const rounded = Math.round(value * 10) / 10;
          const option = document.createElement('option');
          option.value = String(rounded);
          option.textContent = `${rounded}"`;
          select.appendChild(option);
        }

        select.dataset.populated = 'true';
      }

      setCategory(category) {
        if (!category || (category !== 'tops' && category !== 'bottoms')) category = 'tops';
        this.activeCategory = category;

        this.categoryTabs.forEach((tab) => {
          const isActive = tab.dataset.categoryTab === category;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.querySelectorAll('[data-category-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.categoryPanel !== category;
        });

        if (this.activeView === 'chart') {
          this.renderChart(category);
        }
      }

      setView(view) {
        if (!view || (view !== 'quiz' && view !== 'chart')) view = 'quiz';
        this.activeView = view;

        this.secondaryTabs.forEach((tab) => {
          const isActive = tab.dataset.viewTab === view;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.querySelectorAll('[data-category-panel]').forEach((panel) => {
          panel.querySelectorAll('[data-view]').forEach((el) => {
            el.hidden = el.dataset.view !== view;
          });
        });

        if (view === 'chart' && this.activeCategory) {
          this.renderChart(this.activeCategory);
        }
      }

      renderChart(category) {
        const body = this.querySelector(`[data-chart-body="${category}"]`);
        if (!body || body.dataset.rendered || !this.chartData) return;

        const data = this.chartData[category];
        if (!data) return;

        const rows = data.sizes
          .map((size) => {
            if (category === 'tops') {
              const bustRange = `${size.min}–${size.max}"`;
              return `<tr><td>${size.size}</td><td>${bustRange}</td><td>${size.cupRangeLabel}</td></tr>`;
            }
            const waistRange = `${size.waistMin}–${size.waistMax}"`;
            const hipRange = `${size.hipMin}–${size.hipMax}"`;
            return `<tr><td>${size.size}</td><td>${waistRange}</td><td>${hipRange}</td></tr>`;
          })
          .join('');

        body.innerHTML = rows;
        body.dataset.rendered = 'true';
      }

      validateForm(form) {
        const category = form.dataset.quiz;
        const calculateButton = form.querySelector('[data-calculate]');
        const fields = Array.from(form.querySelectorAll('select[data-field]'));
        const isValid = fields.every((field) => field.value !== '');
        calculateButton.disabled = !isValid;
        return isValid;
      }

      onSubmit(event) {
        event.preventDefault();
        const form = event.currentTarget;
        if (!this.validateForm(form)) return;

        const category = form.dataset.quiz;
        const panel = form.closest('[data-category-panel]');

        if (category === 'tops') {
          const bust = Number(form.querySelector('[data-field="bust"]').value);
          const cup = form.querySelector('[data-field="cup"]').value;
          this.showResult(panel, category, this.recommendTopSize(bust, cup), { bust, cup });
        } else {
          const waist = Number(form.querySelector('[data-field="waist"]').value);
          const hips = Number(form.querySelector('[data-field="hips"]').value);
          this.showResult(panel, category, this.recommendBottomSize(waist, hips), { waist, hips });
        }
      }

      sizeIndex(size) {
        return this.chartData.sizeOrder.indexOf(size);
      }

      matchInterval(value, min, max, maxInclusive) {
        if (maxInclusive) return value >= min && value <= max;
        return value >= min && value < max;
      }

      recommendTopSize(bust, cup) {
        const data = this.chartData.tops;

        let bustSize = null;
        for (const entry of data.sizes) {
          if (this.matchInterval(bust, entry.min, entry.max, entry.maxInclusive)) {
            bustSize = entry.size;
            break;
          }
        }

        const cupSize = data.cupMinSize[cup] || null;

        if (!bustSize || !cupSize) return null;

        const finalSize =
          this.sizeIndex(cupSize) > this.sizeIndex(bustSize) ? cupSize : bustSize;

        return finalSize;
      }

      recommendBottomSize(waist, hips) {
        const data = this.chartData.bottoms;

        let waistSize = null;
        let hipSize = null;

        for (const entry of data.sizes) {
          if (this.matchInterval(waist, entry.waistMin, entry.waistMax, entry.waistMaxInclusive)) {
            waistSize = entry.size;
            break;
          }
        }
        for (const entry of data.sizes) {
          if (this.matchInterval(hips, entry.hipMin, entry.hipMax, entry.hipMaxInclusive)) {
            hipSize = entry.size;
            break;
          }
        }

        if (!waistSize || !hipSize) return null;

        return this.sizeIndex(hipSize) > this.sizeIndex(waistSize) ? hipSize : waistSize;
      }

      showResult(panel, category, size, measurements) {
        const quizView = panel.querySelector('[data-view="quiz"]');
        const resultEl = panel.querySelector('[data-result]');
        const form = panel.querySelector(`[data-quiz="${category}"]`);
        const sizeEl = resultEl.querySelector('[data-result-size]');
        const basisEl = resultEl.querySelector('[data-result-basis]');
        const messageEl = resultEl.querySelector('[data-result-message]');
        const selectButton = resultEl.querySelector('[data-select-size]');

        form.hidden = true;
        resultEl.hidden = false;

        if (!size) {
          sizeEl.hidden = true;
          basisEl.hidden = true;
          selectButton.hidden = true;
          messageEl.hidden = false;
          messageEl.textContent =
            this.strings.noMatch ||
            "We couldn't find an exact match in the current size chart. Please contact us at support@samba-swim.com for personalized sizing help.";
          this.announce(messageEl.textContent);
          return;
        }

        sizeEl.hidden = false;
        basisEl.hidden = false;
        sizeEl.textContent = size;

        if (category === 'tops') {
          basisEl.textContent = (this.strings.basedOnTops || 'Based on a {bust}-inch bust and {cup} cup.')
            .replace('{bust}', measurements.bust)
            .replace('{cup}', measurements.cup);
        } else {
          basisEl.textContent = (
            this.strings.basedOnBottoms || 'Based on a {waist}-inch waist and {hips}-inch hip measurement.'
          )
            .replace('{waist}', measurements.waist)
            .replace('{hips}', measurements.hips);
        }

        this.applyVariantAvailability(resultEl, size, messageEl, selectButton);

        this.announce(`${this.strings.recommendedSizeIs || 'Your recommended size is'} ${size}. ${basisEl.textContent}`);
      }

      applyVariantAvailability(resultEl, size, messageEl, selectButton) {
        messageEl.hidden = true;
        selectButton.hidden = true;

        const context = this.getVariantContext();
        if (!context) return; // No matching size option on this product — recommendation only.

        const { sizeField, sizeValues, otherSelections } = context;
        const matchedValue = sizeValues.find((value) => value.trim().toUpperCase() === size);

        if (!matchedValue) return; // Product doesn't offer this size — recommendation only.

        const variant = this.findVariant(otherSelections, this.sizeOptionPosition, matchedValue);

        selectButton.hidden = false;
        selectButton.textContent = (this.strings.selectSize || 'Select Size {size}').replace('{size}', size);
        selectButton.dataset.size = matchedValue;

        if (!variant) {
          // No variant exists for this combination (e.g. sold out and hidden) — treat like sold out.
          selectButton.hidden = true;
          messageEl.hidden = false;
          messageEl.textContent = (this.strings.soldOut || 'Your recommended size {size} is currently sold out.').replace(
            '{size}',
            size
          );
          return;
        }

        if (!variant.available) {
          selectButton.hidden = true;
          messageEl.hidden = false;
          messageEl.textContent = (this.strings.soldOut || 'Your recommended size {size} is currently sold out.').replace(
            '{size}',
            size
          );
        }
      }

      getMainProductInfo() {
        if (!this.productId) return null;
        return document.querySelector(`product-info[data-product-id="${this.productId}"]`);
      }

      getVariantContext() {
        if (this.sizeOptionPosition === null || Number.isNaN(this.sizeOptionPosition)) return null;

        const productInfo = this.getMainProductInfo();
        if (!productInfo) return null;

        const variantSelects = productInfo.querySelector('variant-selects');
        if (!variantSelects) return null;

        const sizeField = variantSelects.querySelector('[data-option-role="size"]');
        if (!sizeField) return null;

        // sizeField is always the wrapper (a <fieldset> for button/swatch pickers, or a
        // <div> for dropdown pickers) — never a <select> itself, so look for one inside it.
        const sizeSelect = sizeField.tagName === 'SELECT' ? sizeField : sizeField.querySelector('select');
        const sizeValues = sizeSelect
          ? Array.from(sizeSelect.querySelectorAll('option')).map((option) => option.value)
          : Array.from(sizeField.querySelectorAll('input[type="radio"]')).map((input) => input.value);

        const controls = Array.from(variantSelects.querySelectorAll('select, fieldset'));
        const otherSelections = controls.map((control) => {
          if (control.tagName === 'SELECT') return control.value;
          const checked = control.querySelector('input:checked');
          return checked ? checked.value : null;
        });

        return { sizeField, sizeValues, otherSelections, controls };
      }

      findVariant(otherSelections, sizeIndex, sizeValue) {
        const target = otherSelections.slice();
        target[sizeIndex] = sizeValue;

        return this.variants.find((variant) =>
          target.every((value, index) => value === null || variant.options[index] === value)
        );
      }

      onRecalculate(event) {
        const panel = event.currentTarget.closest('[data-category-panel]');
        const resultEl = panel.querySelector('[data-result]');
        const category = panel.dataset.categoryPanel;
        const form = panel.querySelector(`[data-quiz="${category}"]`);

        resultEl.hidden = true;
        form.hidden = false;
      }

      onSelectSize(event) {
        const button = event.currentTarget;
        const size = button.dataset.size;
        if (!size) return;

        const context = this.getVariantContext();
        if (!context) return;

        const { sizeField } = context;
        const sizeSelect = sizeField.tagName === 'SELECT' ? sizeField : sizeField.querySelector('select');

        if (sizeSelect) {
          sizeSelect.value = size;
          sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          const input = Array.from(sizeField.querySelectorAll('input[type="radio"]')).find(
            (radio) => radio.value === size
          );
          if (!input) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const modal = this.closest('basic-modal');
        modal?.hide();

        setTimeout(() => {
          sizeField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 350);
      }

      announce(message) {
        if (!this.liveRegion) return;
        this.liveRegion.textContent = '';
        requestAnimationFrame(() => {
          this.liveRegion.textContent = message;
        });
      }
    }
  );
}

// Lets a plain "Find My Size" link inside the Size & Fit accordion's richtext
// content (which can't carry data-/class attributes past Shopify's richtext
// sanitizer) open the same modal as the real .open-size-finder trigger,
// without needing to know its element id.
document.addEventListener('click', (event) => {
  const link = event.target.closest('.accordion-details__content a');
  if (!link || link.textContent.trim() !== 'Find My Size') return;
  event.preventDefault();
  document.querySelector('.open-size-finder')?.click();
});
